<template>
  <ev-tabs
    :options="{
      contentClass: 'main-content',
      tabClass: 'tab-links-nested tab-link-chat',
      defaultTabHash: defaultTabHash
    }"
  >
    <ev-tab
      name="The Room"
      id="tab-chat-room"
      class="tab-chat chat-room"
      v-show="room && !room.ROOM_CHAT_DISABLED"
      :isVisible="!room.ROOM_CHAT_DISABLED"
    >
      <digital-event-room-room-messages :loading="loading"></digital-event-room-room-messages>
    </ev-tab>
    <ev-tab
      :name="`My ${groupTerminology}`"
      id="tab-chat-table"
      class="tab-chat chat-table"
      @click="chatMode = 'TABLE'"
      v-if="isNetworkingAllowed"
      :total="tableMessagesCount"
      :counter="unreadMessagesCounter.TABLE"
    >
      <digital-event-room-table-messages></digital-event-room-table-messages>
    </ev-tab>
    <ev-tab
      name="Private"
      id="tab-chat-private"
      class="tab-chat chat-private"
      icon='<i class="fa fa-fw fa-lg fa-user-circle-o" aria-hidden="true"></i>'
      @click="chatMode = 'PRIVATE'"
      v-if="isNetworkingAllowed"
      :total="myNetworkFilterCounter + otherFilterCounter"
      :counter="unreadMessagesCounter.PRIVATE"
    >
      <digital-event-room-private-messages></digital-event-room-private-messages>
    </ev-tab>
  </ev-tabs>
</template>

<script>
import PrivateMessages from "./Chat/PrivateMessages";
import TableMessages from "./Chat/TableMessages";
import RoomMessages from "./Chat/RoomMessages";
import Tabs from "@/components/DigitalEvent/common/Tabs";
import Tab from "@/components/DigitalEvent/common/Tab";
import { CHAT_MODES, TABLE_TYPES } from "@/helpers/constants";
import { scrollToTheBottom } from "@/helpers/DOMHelper";
import { mapGetters } from "vuex";

export default {
  name: "Chat",
  components: {
    "digital-event-room-table-messages": TableMessages,
    "digital-event-room-private-messages": PrivateMessages,
    "digital-event-room-room-messages": RoomMessages,
    "ev-tabs": Tabs,
    "ev-tab": Tab
  },
  data() {
    return {
      loading: false
    };
  },
  computed: {
    ...mapGetters({
      groupTerminology: "room/groupTerminology",
      room: "room/activeRoom"
    }),
    userTable() {
      return this.$store.getters["tables/userTable"];
    },
    roomUUID() {
      return this.$store.getters["room/activeRoom"].uuid;
    },
    tableUsers() {
      return this.$store.getters["tables/tableUsers"](this.userTable.uuid);
    },
    activeUsers() {
      return this.tableUsers.filter(u => u.isOnline).length;
    },
    inactiveUsers() {
      return this.tableUsers.filter(u => !u.isOnline).length;
    },
    user() {
      return this.$store.state.users.user;
    },
    connections() {
      return this.$store.state.users.userConnections;
    },
    currentUser() {
      return this.$store.state.users.user;
    },
    unreadMessagesCounter() {
      return this.$store.state.chat.unreadMessagesCounter;
    },
    tableMessagesCount() {
      return this.$store.state.chat.tableMessages.length;
    },
    myNetworkFilterCounter() {
      return this.$store.getters["chat/networkMessagesCounter"];
    },
    otherFilterCounter() {
      return this.$store.getters["chat/otherMessagesCounter"];
    },
    privateMessagesCount() {
      return this.$store.getters["chat/privateMessages"].length;
    },
    chatMode: {
      get() {
        return this.$store.state.chat.chatMode;
      },
      set(value) {
        this.$store.commit("chat/changeChatMode", {
          mode: value,
          scrollPage: false
        });

        this.$store.commit("chat/resetUnreadMessagesCounter");
        if (value === CHAT_MODES.PRIVATE) {
          this.$store.commit("chat/setPrivateChatTarget", null);
          scrollToTheBottom("privateMessages");
        } else {
          scrollToTheBottom(`group-${this.userTable.uuid}-messages`);
        }
      }
    },
    isFilterActive() {
      return this.$store.state.chat.privateChatTarget;
    },
    defaultTabHash() {
      return this.room.ROOM_CHAT_DISABLED ? "tab-chat-table" : null;
    },
    isNetworkingAllowed() {
      return this.$store.getters["tables/userTable"].TABLE_TYPE !== TABLE_TYPES.PUBLIC_GALLERY;
    },
    roomMessages() {
      return this.$store.getters["chat/roomMessages"];
    }
  },
  async created() {
    this.loading = true;
    await this.$store.dispatch("chat/getRoomMessages");
    await this.$store.dispatch("chat/getTableMessages");
    await this.$store.dispatch("chat/getPrivateMessagesHistory");
    this.loading = false;
  },
  mounted() {
    /**
     * listen for tab changes and scroll to bottom of message lists
     */
    this.$root.$on("clicked", tab => {
      if (tab.hash === "#tab-chat-private") {
        this.resetPrivateChatStream();
      }
    });
    this.$root.$on("changedTab", tabHash => {
      if (tabHash === "#tab-chat-private") {
        scrollToTheBottom("privateMessages");
      } else if (tabHash === "#tab-chat-room") {
        scrollToTheBottom(`group-${this.roomUUID}-messages`);
      } else if (tabHash === "#tab-chat-table") {
        scrollToTheBottom(`group-${this.userTable.uuid}-messages`);
      }
      // TODO: EVESSIO-9228 - Remove chatMode logic and rework tabs change
      if (tabHash === "#tab-chat-private") {
        this.$store.commit("chat/changeChatMode", {
          mode: CHAT_MODES.PRIVATE,
          scrollPage: false
        });
        this.resetPrivateChatStream();
      } else if (tabHash === "#tab-chat-table") {
        this.$store.commit("chat/resetUnreadMessagesCounter", CHAT_MODES.TABLE);
        this.$store.commit("chat/changeChatMode", {
          mode: CHAT_MODES.TABLE,
          scrollPage: false
        });
      } else {
        this.$store.commit("chat/changeChatMode", {
          mode: null,
          scrollPage: false
        });
      }
    });
  },
  methods: {
    clearFilter() {
      this.$store.commit("chat/setPrivateChatTarget", null);
    },
    isCurrentUser(user) {
      return this.currentUser.user_uuid === user.userUUID;
    },
    resetPrivateChatStream() {
      if (this.$store.state.chat.unreadMessagesCounter.PRIVATE) {
        // reset message stream if there are new messages
        this.$store.commit("chat/resetUnreadMessagesCounter", CHAT_MODES.PRIVATE);
        this.clearFilter();
      }
    }
  }
};
</script>
